import React, { useCallback, useMemo, useState } from "react";
import {
  Control,
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  UseFormGetValues,
  UseFormSetValue,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import Select from "react-select";
import { LuAlertCircle, LuPackage, LuPlus, LuTrash2 } from "react-icons/lu";
import { toast } from "react-toastify";

import { compareSizes } from "../../static/itemSize";
import { PartyTypeProps } from "../../static/partyType";
import {
  RateField,
  compareCodes,
  hasRate,
  rateField,
  rateLabel,
  rateNumber,
  rateText,
} from "../../static/processRate";
import { iconSelectStyles, toastStyles } from "../../static/styles";
import { ItemsProps } from "../../types/item";
import {
  PartyRateGroupInput,
  PartyRatePayload,
  PartyRateProps,
  PartyRateRowInput,
  PartyRatesFormInput,
} from "../../types/parties";
import Button from "../ui/Button";
import cx from "../ui/cx";
import {
  arrayAddButtonClass,
  arrayRemoveButtonClass,
  arrayRowClass,
  buttonSpinnerClass,
  cardContentClass,
  fieldErrorTextClass,
  fieldLabelClass,
  formCardClass,
  formSelectIconClass,
  gradientSubmitButtonClass,
} from "../ui/styles";
import PartyItemRates, {
  CommonRates,
  PartyRateSegment,
  RatePath,
  RowCommonRates,
  emptyCommonRates,
} from "./PartyItemRates";

// A main item with its sub items, in the order the item page shows them.
type CatalogEntry = { item: ItemsProps; subItems: ItemsProps[] };

// One of an item's lines in its row: one of its sizes, or the item itself when it has none.
type ItemLine = Pick<PartyRateRowInput, "size_id" | "size_name">;

type ItemOption = {
  value: number;
  label: string;
  description: string | null;
  subItemCount: number;
  // The item's code, description and sub item codes, so a sub item's code finds its main item.
  search: string;
};

// Like the sub item rows of the item page.
const rowIndexClass =
  "md:col-span-12 flex items-center justify-between gap-2 text-sm font-semibold text-[#113F67]";

const rowElementId = (index: number) => `party-rate-row-${index}`;

// A rate's place: an item without sizes, or one size of an item.
const rateKey = (itemId: number, sizeId: number | null) =>
  `${itemId}-${sizeId ?? ""}`;

const typeFields = (types: PartyTypeProps[]) =>
  types.map((type) => rateField(type.value));

const emptyGroup = (): PartyRateGroupInput => ({ item_id: null, rows: [] });

// An item's lines: one per size it has on the item page, in reading order (12.5 before 94 before 120), or one for the
// item itself. A size listed twice gets one line. The item's own list is left as it is.
const itemLines = (item: ItemsProps): ItemLine[] => {
  const lines = new Map<number, ItemLine>();

  (item.sizes ?? []).forEach((itemSize) => {
    if (itemSize.size && !lines.has(itemSize.size_id)) {
      lines.set(itemSize.size_id, {
        size_id: itemSize.size_id,
        size_name: itemSize.size.name,
      });
    }
  });

  return lines.size > 0
    ? Array.from(lines.values()).sort((a, b) =>
        compareSizes(a.size_name ?? "", b.size_name ?? "")
      )
    : [{ size_id: null, size_name: null }];
};

// Main items in code order, each with its sub items.
const buildCatalog = (items: ItemsProps[]): CatalogEntry[] => {
  const subItems = new Map<number, ItemsProps[]>();

  items.forEach((item) => {
    if (item.parent_id !== null) {
      subItems.set(item.parent_id, [
        ...(subItems.get(item.parent_id) || []),
        item,
      ]);
    }
  });

  return items
    .filter((item) => item.parent_id === null)
    .sort((a, b) => compareCodes(a.name, b.name))
    .map((item) => ({
      item,
      subItems: (subItems.get(item.id) || []).sort((a, b) => a.id - b.id),
    }));
};

// A row's items, each with its lines and where they start among the row's lines: the item itself, or each of its sub
// items. An item built from sub items has no rates of its own, since it never goes to cutting, turning or VMC.
const segmentsFor = (entry: CatalogEntry): PartyRateSegment[] => {
  const items = entry.subItems.length > 0 ? entry.subItems : [entry.item];
  let start = 0;

  return items.map((item) => {
    const lines = itemLines(item);
    const segment = { item, start, lines };
    start += lines.length;
    return segment;
  });
};

// The party's saved rates by place.
const savedRates = (rates: PartyRateProps[]) =>
  new Map(rates.map((rate) => [rateKey(rate.item_id, rate.size_id), rate]));

// An item's row: its lines, then each sub item's. Saved rates fill the party's types only, so an item picked again
// gets back the party's own rates; the item's rates never come in.
const groupFor = (
  entry: CatalogEntry,
  types: PartyTypeProps[],
  saved?: Map<string, PartyRateProps>
): PartyRateGroupInput => {
  const fields = typeFields(types);
  const text = (rate: PartyRateProps | undefined, field: RateField) =>
    fields.includes(field) ? rateText(rate?.[field]) : "";

  return {
    item_id: entry.item.id,
    rows: segmentsFor(entry).flatMap(({ item, lines }) =>
      lines.map((line) => {
        const rate = saved?.get(rateKey(item.id, line.size_id));

        return {
          item_id: item.id,
          ...line,
          cutting_rate: text(rate, "cutting_rate"),
          turning_rate: text(rate, "turning_rate"),
          vmc_rate: text(rate, "vmc_rate"),
        };
      })
    ),
  };
};

// The rows for the saved rates, in code order. A sub item or size added to an item later gets an empty line. With
// nothing saved there is one empty row to pick the first item in, like a new item's first sub item row; without any
// items there is nothing to pick.
const defaultGroups = (
  catalog: CatalogEntry[],
  rates: PartyRateProps[],
  types: PartyTypeProps[],
  saved: Map<string, PartyRateProps>
) => {
  const mainIds = new Set(
    rates.map((rate) => rate.item.parent_id ?? rate.item_id)
  );
  const groups = catalog
    .filter((entry) => mainIds.has(entry.item.id))
    .map((entry) => groupFor(entry, types, saved));

  return groups.length > 0 || catalog.length === 0 ? groups : [emptyGroup()];
};

// 0 is a rate; only a blank box has none.
export const groupHasRate = (
  rows: PartyRateRowInput[] | undefined,
  types: PartyTypeProps[]
) =>
  !!rows?.some((row) =>
    typeFields(types).some((field) => hasRate(row[field]))
  );

// The rows that have a rate, as the server takes them. Rates of types the party doesn't have stay out.
export const ratePayload = (
  groups: PartyRateGroupInput[],
  types: PartyTypeProps[]
): PartyRatePayload[] => {
  const fields = typeFields(types);
  const rate = (row: PartyRateRowInput, field: RateField) =>
    fields.includes(field) ? rateNumber(row[field]) : null;

  return groups
    .flatMap((group) => group.rows)
    .map((row) => ({
      item_id: row.item_id,
      size_id: row.size_id,
      cutting_rate: rate(row, "cutting_rate"),
      turning_rate: rate(row, "turning_rate"),
      vmc_rate: rate(row, "vmc_rate"),
    }))
    .filter((row) => fields.some((field) => row[field] !== null));
};

// By item, the rates it kept from before it had sizes, as its table warns about them. Saving drops them, like the
// item's own rates on the item page.
const droppedRates = (rates: PartyRateProps[], types: PartyTypeProps[]) => {
  const dropped = new Map<number, string[]>();

  rates
    .filter((rate) => rate.stale)
    .forEach((rate) => {
      const texts = types.flatMap((type) => {
        const text = rateText(rate[rateField(type.value)]);
        return hasRate(text) ? [`${rateLabel(type)} ${text}`] : [];
      });

      if (texts.length > 0) dropped.set(rate.item_id, texts);
    });

  return dropped;
};

const itemOption = (entry: CatalogEntry): ItemOption => ({
  value: entry.item.id,
  label: entry.item.name,
  description: entry.item.description,
  subItemCount: entry.subItems.length,
  search: [
    entry.item.name,
    entry.item.description,
    ...entry.subItems.map((subItem) => subItem.name),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase(),
});

type RowProps = {
  control: Control<PartyRatesFormInput>;
  getValues: UseFormGetValues<PartyRatesFormInput>;
  setValue: UseFormSetValue<PartyRatesFormInput>;
  index: number;
  // Every main item, as options and by id with its sub items.
  options: ItemOption[];
  entryById: Map<number, CatalogEntry>;
  types: PartyTypeProps[];
  saved: Map<string, PartyRateProps>;
  // Every item's dropped rates, so the prop stays the same between renders.
  dropped: Map<number, string[]>;
  showMissing: boolean;
  onRemove: (index: number) => void;
};

// A row: its item select, then the item's rates, or for an item built from sub items each sub item's, under a common
// row for them all when there are several. It watches only its own values, so typing in one row doesn't redraw the
// others.
const PartyRateRow = React.memo(
  ({
    control,
    getValues,
    setValue,
    index,
    options,
    entryById,
    types,
    saved,
    dropped,
    showMissing,
    onRemove,
  }: RowProps) => {
    const itemId = useWatch({ control, name: `groups.${index}.item_id` });
    const rows = useWatch({ control, name: `groups.${index}.rows` }) || [];

    // The items picked in the other rows, read when the menu opens.
    const [takenIds, setTakenIds] = useState<Set<number>>(() => new Set());

    // The common rows' rates, which only help typing and start blank: the row's own, and each size table's by item.
    const [rowCommonRates, setRowCommonRates] = useState(emptyCommonRates);
    const [tableCommonRates, setTableCommonRates] = useState<
      Record<number, CommonRates>
    >({});

    const entry = itemId !== null ? entryById.get(itemId) : undefined;
    const segments = useMemo(() => (entry ? segmentsFor(entry) : []), [entry]);
    const value = useMemo(
      () => options.find((option) => option.value === itemId) || null,
      [options, itemId]
    );
    const available = useMemo(
      () => options.filter((option) => !takenIds.has(option.value)),
      [options, takenIds]
    );
    const isMissing =
      showMissing && itemId !== null && !groupHasRate(rows, types);

    const handleMenuOpen = () => {
      setTakenIds(
        new Set(
          getValues("groups").flatMap((group, groupIndex) =>
            groupIndex !== index && group.item_id !== null
              ? [group.item_id]
              : []
          )
        )
      );
    };

    // Stays the same between renders, so the item tables don't redraw on every keystroke.
    const handleTableCommonRate = useCallback(
      (id: number, field: RateField, text: string) => {
        setTableCommonRates((commons) => ({
          ...commons,
          [id]: { ...(commons[id] ?? emptyCommonRates), [field]: text },
        }));
      },
      []
    );

    // The row's common rate goes to that process on every line of the row, with or without sizes, a blank one too.
    // Each size table's common row shows it as well, since all its sizes now have it. Each rate can still be changed
    // on its own.
    const handleRowCommonRate = (field: RateField, text: string) => {
      setRowCommonRates((rates) => ({ ...rates, [field]: text }));
      setTableCommonRates((commons) => {
        const filled = { ...commons };
        segments.forEach(({ item }) => {
          filled[item.id] = {
            ...(commons[item.id] ?? emptyCommonRates),
            [field]: text,
          };
        });
        return filled;
      });

      getValues(`groups.${index}.rows`).forEach((row, rowIndex) => {
        const name = `groups.${index}.rows.${rowIndex}.${field}` as RatePath;
        if (row[field] !== text) setValue(name, text, { shouldDirty: true });
      });
    };

    // A different item brings its own lines, with the party's saved rates for it, and blank common rows.
    const handleItemChange = (
      option: ItemOption | null,
      onChange: (id: number | null) => void
    ) => {
      const id = option?.value ?? null;
      // Picking the row's own item again, such as Tab with the menu open, keeps the rates typed in it.
      if (id === itemId) return;

      const picked = id !== null ? entryById.get(id) : undefined;
      onChange(id);
      setValue(
        `groups.${index}.rows`,
        picked ? groupFor(picked, types, saved).rows : []
      );
      setRowCommonRates(emptyCommonRates);
      setTableCommonRates({});
    };

    return (
      <div
        id={rowElementId(index)}
        className={cx(arrayRowClass, isMissing && "border-2 border-red-500")}
      >
        <div className={rowIndexClass}>
          <span>આઈટમ - {index + 1}</span>
          <button
            type="button"
            title="કાઢી નાખો"
            className={arrayRemoveButtonClass}
            onClick={() => onRemove(index)}
          >
            <LuTrash2 className="h-5 w-5" />
          </button>
        </div>

        <div className="md:col-span-12 space-y-2">
          <label
            htmlFor={`party-rate-${index}-item`}
            className={fieldLabelClass}
          >
            આઈટમ <span className="text-red-500">*</span>
          </label>
          <Controller
            control={control}
            name={`groups.${index}.item_id`}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <>
                <div className={cx("relative", fieldState.error && "error")}>
                  <LuPackage className={formSelectIconClass} />
                  <Select
                    ref={field.ref}
                    inputId={`party-rate-${index}-item`}
                    value={value}
                    options={available}
                    placeholder="આઈટમ નો કોડ લખો અથવા પસંદ કરો"
                    noOptionsMessage={() =>
                      options.length > 0 && available.length === 0
                        ? "બધી આઈટમ ઉમેરાઈ ગઈ છે"
                        : "આઈટમ મળી નથી"
                    }
                    filterOption={(option: any, input: string) =>
                      option.data.search.includes(input.trim().toLowerCase())
                    }
                    // The description only in the menu, so a picked item fits on one line.
                    formatOptionLabel={(option: any, { context }: any) => (
                      <div className="flex min-w-0 flex-col">
                        <span className="font-medium">
                          {option.label}
                          {option.subItemCount > 0 && (
                            <span className="ml-2 text-xs font-normal text-gray-500">
                              ({option.subItemCount} સબ આઈટમ)
                            </span>
                          )}
                        </span>
                        {context === "menu" && option.description && (
                          <span className="truncate text-xs text-gray-500">
                            {option.description}
                          </span>
                        )}
                      </div>
                    )}
                    styles={iconSelectStyles}
                    isSearchable
                    className={cx(
                      "react-select-container",
                      fieldState.error && "error"
                    )}
                    classNamePrefix="react-select"
                    onMenuOpen={handleMenuOpen}
                    onChange={(option: any) =>
                      handleItemChange(option, field.onChange)
                    }
                    onBlur={field.onBlur}
                  />
                </div>
                {fieldState.error ? (
                  <p className={fieldErrorTextClass}>
                    <LuAlertCircle className="h-4 w-4 mr-1" />
                    આઈટમ પસંદ કરો
                  </p>
                ) : (
                  entry?.item.description && (
                    <p className="break-words text-xs text-gray-500">
                      {entry.item.description}
                    </p>
                  )
                )}
              </>
            )}
          />
        </div>

        {/* A row with one set of rates (an item, or its only sub item) has its table's own common row, or a single
            set of boxes. */}
        {entry && segments.length > 1 && (
          <RowCommonRates
            item={entry.item}
            types={types}
            rates={rowCommonRates}
            onChange={handleRowCommonRate}
          />
        )}

        {segments.length > 0 && (
          <div className="md:col-span-12 space-y-4">
            {segments.map((segment, segmentIndex) => (
              <PartyItemRates
                key={segment.item.id}
                control={control}
                getValues={getValues}
                setValue={setValue}
                groupIndex={index}
                segment={segment}
                types={types}
                // Each sub item says whose rates these are, also when it is the only one.
                isSubItem={!!entry && entry.subItems.length > 0}
                first={segmentIndex === 0}
                dropped={dropped.get(segment.item.id)}
                hasError={isMissing}
                commonRates={
                  tableCommonRates[segment.item.id] ?? emptyCommonRates
                }
                onCommonRate={handleTableCommonRate}
              />
            ))}
          </div>
        )}

        {isMissing && (
          <p className={cx(fieldErrorTextClass, "md:col-span-12")}>
            <LuAlertCircle className="h-4 w-4 mr-1" />
            ઓછામાં ઓછો એક ભાવ દાખલ કરો
          </p>
        )}
      </div>
    );
  }
);

PartyRateRow.displayName = "PartyRateRow";

type Props = {
  // The party's process types; only these get rate boxes.
  types: PartyTypeProps[];
  // Every item, sub items included.
  items: ItemsProps[];
  rates: PartyRateProps[];
  isSubmitting: boolean;
  onSubmit: (rows: PartyRatePayload[]) => void;
  onCancel: () => void;
};

const PartyRatesForm = ({
  types,
  items,
  rates,
  isSubmitting,
  onSubmit,
  onCancel,
}: Props) => {
  const catalog = useMemo(() => buildCatalog(items), [items]);
  const entryById = useMemo(
    () => new Map(catalog.map((entry) => [entry.item.id, entry])),
    [catalog]
  );
  const options = useMemo(() => catalog.map(itemOption), [catalog]);
  const saved = useMemo(() => savedRates(rates), [rates]);
  const dropped = useMemo(() => droppedRates(rates, types), [rates, types]);

  const { control, getValues, setValue, handleSubmit } =
    useForm<PartyRatesFormInput>({
      defaultValues: { groups: defaultGroups(catalog, rates, types, saved) },
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "groups",
  });

  // Rows without a rate are marked once Save has been tried.
  const [showMissing, setShowMissing] = useState(false);

  const handleSave: SubmitHandler<PartyRatesFormInput> = (data) => {
    const missing = data.groups.findIndex(
      (group) => !groupHasRate(group.rows, types)
    );
    setShowMissing(true);

    if (missing !== -1) {
      toast.error("દરેક આઈટમ માં ઓછામાં ઓછો એક ભાવ દાખલ કરો", {
        ...(toastStyles as any),
      });
      document
        .getElementById(rowElementId(missing))
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    onSubmit(ratePayload(data.groups, types));
  };

  // A row without an item: its select shows it and gets the focus. Rows without a rate are marked as well.
  const handleInvalid: SubmitErrorHandler<PartyRatesFormInput> = () => {
    setShowMissing(true);
    toast.error("આઈટમ પસંદ કરો", { ...(toastStyles as any) });
  };

  return (
    <div className={formCardClass}>
      <div className={cx(cardContentClass, "pt-6")}>
        <form
          onSubmit={handleSubmit(handleSave, handleInvalid)}
          className="space-y-6"
        >
          <div className="space-y-4">
            <div className="space-y-1">
              <div className={fieldLabelClass}>આઈટમ ભાવ</div>
              <p className="text-xs text-gray-500">
                સબ આઈટમ વાળી આઈટમ પસંદ કરતાં તેની બધી સબ આઈટમ આવી જશે, ભાવ દરેક
                સબ આઈટમ માં દાખલ કરો. સાઈઝ વાળી આઈટમ કે સબ આઈટમ માં દરેક સાઈઝ નો
                ભાવ અલગ દાખલ કરો. ભાવ ખાલી રાખી શકાય, પણ દરેક આઈટમ માં ઓછામાં
                ઓછો એક ભાવ જોઈએ.
              </p>
            </div>

            {fields.length === 0 ? (
              <div className="space-y-2 rounded-lg border-2 border-dashed border-gray-200 p-8 text-center text-sm text-gray-500">
                <p>
                  {catalog.length > 0
                    ? "હજુ કોઈ આઈટમ ઉમેરી નથી, નીચે થી આઈટમ ઉમેરો"
                    : "કોઈ આઈટમ મળી નથી, પહેલા આઈટમ બનાવો"}
                </p>
                {rates.length > 0 && (
                  <p className="flex items-center justify-center text-orange-700">
                    <LuAlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                    સેવ કરતાં આ પાર્ટી ના બધા ભાવ કાઢી નાખવામાં આવશે
                  </p>
                )}
              </div>
            ) : (
              fields.map((field, index) => (
                <PartyRateRow
                  key={field.id}
                  control={control}
                  getValues={getValues}
                  setValue={setValue}
                  index={index}
                  options={options}
                  entryById={entryById}
                  types={types}
                  saved={saved}
                  dropped={dropped}
                  showMissing={showMissing}
                  onRemove={remove}
                />
              ))
            )}

            {/* Each row has its own item, so there are never more rows than items. */}
            <button
              type="button"
              className={arrayAddButtonClass}
              disabled={fields.length >= catalog.length}
              onClick={() => append(emptyGroup())}
            >
              <LuPlus className="h-4 w-4" />
              આઈટમ ઉમેરો
            </button>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-4 pt-4">
            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                રદ કરો
              </Button>

              <button
                type="submit"
                className={gradientSubmitButtonClass}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className={buttonSpinnerClass}></div>
                    સાચવી રહ્યા છીએ...
                  </div>
                ) : (
                  "સાચવો"
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartyRatesForm;
